import { AlertColor } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import strings from '../../../../common/Translation/Translate';
import Notification from '../../../../components/Notification';
import { FormDataModel } from '../../../../components/Form';
import axiosInstance from '../../../../utils/axios';
import { PageProps } from '../../../../utils/constants/data';
import { privileges } from '../../../../utils/constants/privileges';
import { URLS } from '../../../../utils/constants/urls';
import { filterDataforApi, pageNo, rowCount } from 'utils/redux/reducer/filter-slice';
import ListPage from '../../CommonPage';
import MasterDataEntityDrawer from '../../CommonPage/MasterDataEntityDrawer';
import { lastUpdatedTableColumn } from '../../CommonPage/lastUpdatedColumn';
import { AddTemplateScheduleForm } from './AddEditTemplateSchedule/AddTemplateScheduleForm';

const TemplateScheduleList: FC = () => {
  const index = useSelector(pageNo);
  const filter = useSelector(filterDataforApi);
  const pageSize = useSelector(rowCount);

  const [pageResponse, setPageResponse] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formValues, setFormValues] = useState<FormDataModel>({});
  const [message, setMessage] = useState<{ display: boolean; severity: AlertColor | null; message: string }>({
    display: false,
    severity: null,
    message: '',
  });

  const formModel = useMemo(() => AddTemplateScheduleForm(strings), []);

  const getList = useCallback(
    async (filterData: PageProps) => {
      try {
        setLoading(true);
        const body = filterData
          ? filterData
          : { page: index + 1, size: pageSize, filter: filter ?? [], sort: ['createdAt:desc'] };
        const { data } = await axiosInstance.post(URLS.templateSchedule, body);
        const contentFilter = data.content.map((item: any) => ({ ...item, name: item.template?.type }));
        setPageResponse({ ...data, content: contentFilter });
      } catch (error: any) {
        // ignore load failures, table shows empty state
      } finally {
        setLoading(false);
      }
    },
    [index, pageSize, filter],
  );

  useEffect(() => {
    getList(null as any);
  }, [getList]);

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  const handleCatch = (error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: error?.response?.data?.error_description ?? error?.response?.data?.message ?? error?.message,
    });
  };

  const openDrawer = useCallback(async (recordId: number) => {
    setEditingId(recordId);
    try {
      setDrawerLoading(true);
      const { data } = await axiosInstance.get(URLS.get_edit_delete_templateScheduleById(recordId));
      setFormValues({
        numberOfDays: data?.numberOfDays ?? '',
        mailSubject: data?.mailSubject ?? '',
      });
    } catch (error: any) {
      handleCatch(error);
      return;
    } finally {
      setDrawerLoading(false);
    }
    setDrawerOpen(true);
  }, []);

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingId(null);
    setFormValues({});
  };

  const handleSave = async (body: FormDataModel) => {
    if (!editingId) return;
    try {
      setSaving(true);
      await axiosInstance.put(URLS.get_edit_delete_templateScheduleById(editingId), body);
      setMessage({ display: true, severity: 'success', message: strings.common_update_message });
      closeDrawer();
      getList(null as any);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const pageConfig = useMemo(
    () => ({
      title: strings.templateSchedule_title_list,
      subtitle: strings.templateSchedule_list_subtitle,
      searchField: 'mailScheduleReference',
      searchPlaceholder: 'Search template schedules',
      AddButtonText: '',
      listApi: URLS.templateSchedule,
      deleteApi: null,
      editPrivilege: privileges.edit_template_schedule,
      addPrivilege: null,
      customEdit: (id: number) => openDrawer(id),
      tableColumn: [
        {
          field: 'mailScheduleReference',
          headerName: strings.templateSchedule_column_type,
          flex: 3,
          sortable: true,
          sortValue: 'mailScheduleReference',
        },
        {
          field: 'mailSubject',
          headerName: strings.mailTemplate_text_subject,
          flex: 5,
          sortable: false,
          sortValue: 'mailSubject',
        },
        { field: 'numberOfDays', headerName: strings.templateSchedule_column_name, flex: 2, sortable: true },
        lastUpdatedTableColumn(),
      ],
    }),
    [openDrawer],
  );

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <ListPage
        isLoading={loading}
        isWhiteSpace={false}
        pageConfig={pageConfig}
        pageResponse={pageResponse}
        getList={getList}
      />
      <MasterDataEntityDrawer
        open={drawerOpen}
        title={strings.templateSchedule_title_edit}
        isEdit={Boolean(editingId)}
        formModel={formModel}
        formValues={formValues}
        loading={drawerLoading}
        saving={saving}
        canDelete={false}
        onClose={closeDrawer}
        onSave={handleSave}
        onInvalid={msg => setMessage({ display: true, severity: 'error', message: msg })}
      />
    </>
  );
};
export default TemplateScheduleList;
